import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  getReferencias,
  getDatosExcel,
  getListRefActu,
  insertNewTheoreticalTime,
} from "../../services/ConnectDataBase";
import { SCREEN_IDS } from "../../constants/Constants";
import { typeConnect } from "../../constants/Constants";
import { translateText } from "../../translations/translationService";
import { notify } from "../../services/ui/notify";
import {
  TEXT_COLORS,
  STATUS_COLORS,
  BRAND_COLORS,
  BACKGROUND_COLORS,
} from "../../style/colors.js";

const PROCESOS_PERMITIDOS = ["REPASO", "PULIDO", "BAÑOS", "CALIDAD"];
/** En Control de Calidad solo mostramos REPASO y PULIDO */
const PROCESOS_VISIBLES_CALIDAD = ["REPASO", "PULIDO"];

/** Agrupa por referencia-tipo-mo igual que vista avanzada (InformacionEnDetalle): suma duracion, mantiene cantidad del primer registro */
function agruparDatosPorRefTipoMO(rows) {
  const acc = new Map();
  rows.forEach((item) => {
    const ref = (item.referencia || "").trim();
    const tipo = item.tipo || "";
    const mo = item.mo ?? "";
    const key = `${ref}-${tipo}-${mo}`;
    if (acc.has(key)) {
      const existente = acc.get(key);
      existente.duracion += parseFloat(item.duracion) || 0;
      // cantidad no se suma: se mantiene la del primer registro (igual que vista avanzada)
    } else {
      acc.set(key, {
        ...item,
        referencia: ref,
        tipo,
        mo,
        duracion: parseFloat(item.duracion) || 0,
        cantidad: parseFloat(item.cantidad) || 1,
      });
    }
  });
  return Array.from(acc.values());
}

function buildDetalleDisponible(rows) {
  const map = {};
  const filtrarOutliers = (valores) => {
    if (valores.length < 3) return valores;
    const sorted = [...valores].sort((a, b) => a - b);
    const q1Index = Math.floor(sorted.length * 0.25);
    const q3Index = Math.floor(sorted.length * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    const lowerBound = q1 - 2.5 * iqr;
    const upperBound = q3 + 2.5 * iqr;
    return valores.filter((v) => v >= lowerBound && v <= upperBound);
  };

  rows.forEach((item) => {
    const refCorta = (item.referencia || "").substring(0, 5);
    const key = `${refCorta}|${item.tipo}`;
    if (!PROCESOS_PERMITIDOS.includes(item.tipo)) return;
    const fecha =
      item.fecha_proceso || item.fecha || item.fecha_ini_formateada;
    const tiempoReal = parseFloat(item.duracion) / parseFloat(item.cantidad);
    const tiempoTeorico =
      parseFloat(item.tiempo_teorico) / parseFloat(item.cantidad);
    if (!map[key]) {
      map[key] = {
        existe: true,
        fechas: [fecha],
        tiemposReales: [tiempoReal],
        tiemposTeoricos: [tiempoTeorico],
        totalRegistros: 1,
        tiemposPorMO: {},
      };
    } else {
      if (!map[key].fechas.includes(fecha)) map[key].fechas.push(fecha);
      map[key].tiemposReales.push(tiempoReal);
      map[key].tiemposTeoricos.push(tiempoTeorico);
      map[key].totalRegistros++;
    }
    const mo = item.mo;
    if (!map[key].tiemposPorMO[mo]) {
      map[key].tiemposPorMO[mo] = {
        tiempoTotal: 0,
        cantidadTotal: 0,
        registros: 0,
      };
    }
    map[key].tiemposPorMO[mo].tiempoTotal += parseFloat(item.duracion);
    map[key].tiemposPorMO[mo].cantidadTotal += parseFloat(item.cantidad);
    map[key].tiemposPorMO[mo].registros++;
  });

  Object.keys(map).forEach((key) => {
    const tiemposPorMO = map[key].tiemposPorMO;
    const tiemposPorPieza = Object.values(tiemposPorMO).map(
      (mo) => mo.tiempoTotal / mo.cantidadTotal
    );
    const tiemposFiltrados = filtrarOutliers(tiemposPorPieza);
    map[key].mediaReal =
      tiemposFiltrados.length > 0
        ? Math.round(
            tiemposFiltrados.reduce((sum, t) => sum + t, 0) /
              tiemposFiltrados.length
          )
        : 0;
    map[key].totalValoresMedia = tiemposPorPieza.length;
    map[key].valoresEliminados =
      tiemposPorPieza.length - tiemposFiltrados.length;
  });

  return map;
}

export default function ComponentTiemposProcesosCalidad({ referencia, lang }) {
  const refCorta = (referencia || "").substring(0, 5);
  const [procesos, setProcesos] = useState([]);
  const [detalleDisponible, setDetalleDisponible] = useState({});
  const [referenciasActualizadas, setReferenciasActualizadas] = useState([]);
  const [inputsValores, setInputsValores] = useState({});
  const [metalSeleccionadoPorProceso, setMetalSeleccionadoPorProceso] =
    useState({});
  const [loadingKeys, setLoadingKeys] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!refCorta) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const [refsRes, excelRes, actualizadasRes] = await Promise.all([
        getReferencias("referencias", typeConnect[0], SCREEN_IDS.GENERAL),
        getDatosExcel("TIEMPOS", typeConnect[0], SCREEN_IDS.GENERAL),
        getListRefActu("getReferenciasActu", SCREEN_IDS.GENERAL),
      ]);

      let dataRef = null;
      if (refsRes && typeof refsRes === "object" && !Array.isArray(refsRes)) {
        // Puede venir { status, data: { Oro: {...}, ... } } o directamente { Oro: {...} }
        const raw =
          refsRes.data && typeof refsRes.data === "object" && !Array.isArray(refsRes.data)
            ? refsRes.data
            : refsRes;
        const merged = {};
        Object.keys(raw).forEach((key) => {
          const obj = raw[key];
          if (obj && typeof obj === "object" && !Array.isArray(obj))
            Object.assign(merged, obj);
        });
        // Buscar por ref corta, referencia completa o cualquier clave que coincida con el prefijo
        dataRef =
          merged[refCorta] ||
          merged[referencia] ||
          (() => {
            const foundKey = Object.keys(merged).find(
              (k) =>
                k === refCorta ||
                k === referencia ||
                (typeof k === "string" && k.substring(0, 5) === refCorta)
            );
            return foundKey ? merged[foundKey] : null;
          })();
      }
      const todos = dataRef?.procesos || [];
      const soloRepasoPulido = todos.filter((p) =>
        PROCESOS_VISIBLES_CALIDAD.includes(p.descripcion)
      );
      setProcesos(soloRepasoPulido);

      if (actualizadasRes && Array.isArray(actualizadasRes))
        setReferenciasActualizadas(actualizadasRes);

      const rawData = Array.isArray(excelRes)
        ? excelRes
        : (excelRes && excelRes.data) || [];
      const rowsFiltradas = Array.isArray(rawData)
        ? rawData.filter(
            (r) => (r.referencia || "").substring(0, 5) === refCorta
          )
        : [];
      const rowsAgrupadas = agruparDatosPorRefTipoMO(rowsFiltradas);
      setDetalleDisponible(buildDetalleDisponible(rowsAgrupadas));
    } catch (err) {
      console.error("Error loading tiempos procesos calidad:", err);
      setProcesos([]);
      setDetalleDisponible({});
    } finally {
      setIsLoading(false);
    }
  }, [refCorta, referencia]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const lookupActualizadas = useMemo(() => {
    const map = {};
    referenciasActualizadas.forEach((ref) => {
      const prefijo = (ref.referencia || "").substring(0, 5);
      const key = `${prefijo}-${ref.codigo}`;
      map[key] = ref;
    });
    return map;
  }, [referenciasActualizadas]);

  const handleInputChange = useCallback((codigo, value) => {
    setInputsValores((prev) => ({ ...prev, [codigo]: value }));
  }, []);

  const handleMetalChange = useCallback((codigo, metal) => {
    setMetalSeleccionadoPorProceso((prev) => ({ ...prev, [codigo]: metal }));
  }, []);

  const actualizarTiempo = useCallback(
    async (codigo) => {
      const valor = inputsValores[codigo];
      const metalSeleccionado = metalSeleccionadoPorProceso[codigo] || "Oro";
      if (!valor || isNaN(parseFloat(valor))) {
        notify.warn(translateText("Introduce un valor válido para el tiempo", lang));
        return;
      }
      setLoadingKeys((prev) => ({ ...prev, [codigo]: true }));
      try {
        const response = await insertNewTheoreticalTime(
          "insertTheoreticaltime",
          SCREEN_IDS.GENERAL,
          refCorta,
          codigo,
          valor,
          metalSeleccionado
        );
        if (response !== false && response != null) {
          notify.success(
            translateText("Tiempo actualizado correctamente", lang) +
              " (" +
              metalSeleccionado +
              ")"
          );
          fetchData();
        } else {
          notify.error(translateText("Error al actualizar el tiempo", lang));
        }
      } catch (e) {
        console.error(e);
        notify.error(translateText("Error al actualizar el tiempo", lang));
      } finally {
        setLoadingKeys((prev) => ({ ...prev, [codigo]: false }));
      }
    },
    [
      refCorta,
      inputsValores,
      metalSeleccionadoPorProceso,
      lang,
      fetchData,
    ]
  );

  if (!referencia) return null;

  if (isLoading) {
    return (
      <Box sx={{ py: 1, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {translateText("Cargando tiempos por proceso…", lang)}
        </Typography>
      </Box>
    );
  }

  if (procesos.length === 0) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {translateText("Sin datos de procesos para esta referencia", lang)}
        </Typography>
      </Box>
    );
  }

  const cellSx = {
    padding: "2px 4px",
    fontSize: "0.75rem",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  };
  const headerSx = {
    ...cellSx,
    fontWeight: 600,
    backgroundColor: BACKGROUND_COLORS.TABLE_HEADER || "#f5f5f5",
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: "0.8rem" }}>
        {translateText("Tiempos por proceso para la referencia", lang)}: {refCorta}
      </Typography>
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small" sx={{ tableLayout: "fixed", borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerSx, width: "18%" }}>
                {translateText("Proceso", lang)}
              </TableCell>
              <TableCell sx={{ ...headerSx, width: "16%" }}>
                {translateText("T. Estándar", lang)}
              </TableCell>
              <TableCell sx={{ ...headerSx, width: "20%" }}>
                {translateText("Media Real", lang)}
              </TableCell>
              <TableCell sx={{ ...headerSx, width: "18%" }}>
                {translateText("Metal", lang)}
              </TableCell>
              <TableCell sx={{ ...headerSx, width: "28%" }}>
                {translateText("Actualizar", lang)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {procesos.map((proceso) => {
              const inputKey = proceso.codigo;
              const detailKey = `${refCorta}|${proceso.descripcion}`;
              const detalleInfo = detalleDisponible[detailKey];
              const tiempo =
                proceso.numeropiedras === ".0000" ||
                proceso.numeropiedras == null
                  ? 0
                  : parseFloat(proceso.numeropiedras);
              const lookupKey = `${refCorta}-${proceso.codigo}`;
              const actualizada = lookupActualizadas[lookupKey];

              return (
                <TableRow key={inputKey}>
                  <TableCell sx={cellSx}>{proceso.descripcion}</TableCell>
                  <TableCell sx={cellSx}>{tiempo} min</TableCell>
                  <TableCell sx={cellSx}>
                    {detalleInfo && detalleInfo.mediaReal != null ? (
                      <Box>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: STATUS_COLORS.SUCCESS || "green",
                          }}>
                          {detalleInfo.mediaReal} min
                        </Typography>
                        {(detalleInfo.totalValoresMedia != null || detalleInfo.totalRegistros != null) && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.65rem",
                              color: TEXT_COLORS.SECONDARY,
                              display: "block",
                            }}>
                            (
                            {detalleInfo.totalValoresMedia != null &&
                            (detalleInfo.valoresEliminados || 0) > 0
                              ? `${detalleInfo.totalValoresMedia - (detalleInfo.valoresEliminados || 0)} de ${detalleInfo.totalValoresMedia} `
                              : detalleInfo.totalValoresMedia != null
                                ? `${detalleInfo.totalValoresMedia} `
                                : detalleInfo.totalRegistros != null
                                  ? `${detalleInfo.totalRegistros} `
                                  : ""}
                            reg.)
                          </Typography>
                        )}
                        {(detalleInfo.valoresEliminados || 0) > 0 && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.6rem",
                              color: STATUS_COLORS.WARNING,
                              display: "block",
                            }}>
                            ⚠️ {detalleInfo.valoresEliminados}{" "}
                            {translateText("filtrados", lang)}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.7rem",
                          fontStyle: "italic",
                          color: TEXT_COLORS.TERTIARY,
                        }}>
                        {translateText("Sin datos", lang)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <FormControl size="small" sx={{ minWidth: 64 }}>
                      <Select
                        value={
                          metalSeleccionadoPorProceso[inputKey] || "Oro"
                        }
                        onChange={(e) =>
                          handleMetalChange(inputKey, e.target.value)
                        }
                        sx={{ fontSize: "0.7rem", height: 26 }}
                      >
                        <MenuItem value="Oro" sx={{ fontSize: "0.7rem" }}>
                          🟡 Oro
                        </MenuItem>
                        <MenuItem value="Plata" sx={{ fontSize: "0.7rem" }}>
                          ⚪ Plata
                        </MenuItem>
                        <MenuItem value="Platino" sx={{ fontSize: "0.7rem" }}>
                          ⚫ Platino
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        flexWrap: "wrap",
                      }}>
                      <TextField
                        size="small"
                        type="number"
                        value={inputsValores[inputKey] ?? ""}
                        onChange={(e) =>
                          handleInputChange(inputKey, e.target.value)
                        }
                        inputProps={{
                          step: "any",
                          style: { fontSize: "0.75rem", height: 24 },
                        }}
                        sx={{ minWidth: 72, width: 72 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => actualizarTiempo(inputKey)}
                        disabled={!!loadingKeys[inputKey]}
                        startIcon={
                          loadingKeys[inputKey] ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : null
                        }
                        sx={{
                          fontSize: "0.65rem",
                          minWidth: 68,
                          py: 0.25,
                          px: 0.75,
                          bgcolor: BRAND_COLORS.SECONDARY,
                          "&:hover": {
                            bgcolor: BRAND_COLORS.PRIMARY_DARK,
                          },
                        }}>
                        {loadingKeys[inputKey]
                          ? translateText("Guardando…", lang)
                          : translateText("Actualizar", lang)}
                      </Button>
                      {actualizada && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.8rem",
                            color: TEXT_COLORS.SECONDARY,
                            whiteSpace: "nowrap",
                          }}>
                          {new Date(
                            actualizada.fecha?.date || actualizada.fecha
                          ).toLocaleDateString()}{" "}
                          {parseFloat(
                            actualizada.numeropiedras || 0
                          ).toFixed(1)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
